/**
 * Registro único de tabs + gráficos del Dashboard v2 -- fuente de verdad para
 * DashboardSettings (selector de personalización) y para armar el PDF
 * combinado. Los `id` de gráfico son los MISMOS data-testid ya usados en
 * ChartCard (LeadsPage/MarketingTab/etc.) -- no se inventa un ID paralelo.
 *
 * Lista negra (CLAUDE.md §3 #9): un tab o gráfico que se agregue aquí después
 * sigue visible por defecto para usuarios con dashboard_prefs ya guardadas --
 * solo desaparece si su id está explícitamente en tabs_hidden/charts_hidden.
 */
export const DASHBOARD_SECTIONS = [
  {
    key: 'marketing',
    label: 'Marketing',
    charts: [
      { id: 'mkt-chart-origin-bar', label: 'Origen de leads', description: 'Compara cuántos leads entraron por cada canal (WhatsApp, formulario web, referidos, etc.) en el rango de fecha seleccionado.' },
      { id: 'mkt-chart-monthly', label: 'Captación mensual', description: 'Cantidad de leads nuevos capturados mes a mes durante el último año.' },
      { id: 'mkt-chart-source-pie', label: 'Leads por fuente', description: 'Distribución porcentual de leads por fuente de origen -- mismo dato que "Origen de leads" en formato circular.' },
      { id: 'mkt-chart-channel-campaign', label: 'Leads por canal + campaña', description: 'Cruza canal de entrada con campaña de marketing para ver qué combinación trae más leads.' },
      { id: 'mkt-chart-campaign', label: 'Leads por campaña', description: 'Leads generados por cada campaña activa; se puede filtrar a una campaña específica.' },
      { id: 'mkt-chart-groups', label: 'Leads por grupos', description: 'Distribución de leads por grupo de asignación (equipo de venta).' },
      { id: 'mkt-chart-cpl', label: 'CPL por canal', description: 'Costo por lead (presupuesto ÷ leads) y volumen de leads, por canal de marketing.' },
      { id: 'mkt-chart-daily90', label: 'Tendencia 90 días', description: 'Leads únicos, duplicados y totales día por día en los últimos 90 días.' },
      { id: 'mkt-table-budget', label: 'Presupuesto por campaña activa', description: 'Detalle de presupuesto, leads generados y CPL de cada campaña activa del período.' },
      { id: 'mkt-chart-budget-history', label: 'Presupuesto histórico', description: 'Presupuesto mensual invertido en los últimos 12 meses, desglosado por campaña.' },
    ],
  },
  {
    key: 'ventas',
    label: 'Ventas',
    charts: [
      { id: 'ventas-chart-funnel', label: 'Calificaciones CEM', description: 'Cuántos leads hay en cada nivel de interés/calificación: fríos, nuevos, contactados, calificados, negociación, ganados.' },
      { id: 'ventas-chart-pipeline', label: 'Pipeline de oportunidades', description: 'Oportunidades del CRM agrupadas por etapa del embudo de ventas.' },
      { id: 'ventas-chart-forecast', label: 'Forecast vs venta real', description: 'Compara la meta mensual planeada contra el pipeline ponderado y la venta real cerrada.' },
      { id: 'ventas-chart-scoring', label: 'IA Score vs Seller Score', description: 'Compara la calificación automática de la IA contra la calificación manual del vendedor para cada lead calificado.' },
      { id: 'ventas-chart-activities', label: 'Actividades comerciales', description: 'Llamadas, visitas, mensajes y correos registrados, filtrables por origen (manual, IA o motor de seguimiento).' },
      { id: 'ventas-chart-attribution', label: 'Atribución por canal', description: 'Leads GANADOS (ventas cerradas) agrupados por canal de origen.' },
      { id: 'ventas-chart-assignment', label: 'Distribución de asignación', description: 'Cuántos leads tiene asignado cada vendedor en el período seleccionado.' },
      { id: 'ventas-chart-vendor-stages', label: 'Desempeño por vendedor', description: 'Leads de cada vendedor desglosados por etapa: nuevos, en pipeline, ganados y perdidos.' },
      { id: 'ventas-table-vendors', label: 'Desempeño por vendedor (detalle)', description: 'Tabla detallada por vendedor: ventas, valor de pipeline, tasa de cierre y leads perdidos.' },
      { id: 'ventas-high-frequency', label: 'Leads de alta frecuencia', description: 'Contactos que enviaron más de una solicitud en el período -- señal de interés repetido o posible duplicado.' },
      { id: 'ventas-chart-lost', label: 'Perdidos por categoría', description: 'Leads perdidos agrupados por categoría y motivo específico de pérdida.' },
    ],
  },
  {
    key: 'ia',
    label: 'IA & Seguimiento',
    charts: [
      { id: 'ia-chart-channels', label: 'Mensajes por rol', description: 'Distribución de mensajes de las conversaciones de WhatsApp entre IA, lead y humano (asesor).' },
      { id: 'ia-chart-nurturing', label: 'Reglas de seguimiento', description: 'Estado de las secuencias de seguimiento automático: en cola, entregadas y tasa de éxito del día.' },
    ],
  },
  {
    key: 'direccion',
    label: 'Dirección',
    charts: [
      { id: 'dir-chart-trend', label: 'Tendencia anual', description: 'Leads, ganados y valor de pipeline mes a mes durante los últimos 12 meses.' },
      { id: 'dir-chart-top', label: 'Top 5 vendedores', description: 'Los 5 vendedores con mayor tasa de conversión en el período seleccionado.' },
      { id: 'dir-chart-exec', label: 'Resumen ejecutivo', description: 'Cifras clave del período: leads nuevos, perdidos, pipeline activo y tasa de conversión.' },
    ],
  },
  {
    key: 'callforce',
    label: 'CallForce',
    charts: [
      { id: 'cf-chart-interest-donut', label: 'Distribución de nivel de interés', description: 'Proporción de llamadas por nivel de interés detectado (caliente, tibio, frío, etc.).' },
      { id: 'cf-chart-ranking', label: 'Ranking de vendedores', description: 'Vendedores con mejor y peor score promedio de llamada, más el score de la IA como referencia.' },
      { id: 'cf-chart-mkt-vs-ventas', label: 'Marketing vs Ventas', description: 'Score promedio de llamada por campaña de origen -- solo campañas con 3 o más llamadas analizadas.' },
    ],
  },
];

export const CHART_TYPE_OPTIONS = [
  { value: 'bar', label: 'Barra' },
  { value: 'pie', label: 'Pastel' },
  { value: 'doughnut', label: 'Circular' },
  { value: 'table', label: 'Tabla' },
];

/** Charts cuyo dato ya es un array [{name, value}] simple -- únicos que
 * soportan cambio de tipo de visualización en esta primera versión (ver
 * ChartCard.jsx: renderByType). El resto (series múltiples, stacked,
 * scatter, composed) solo soporta ocultar, no cambiar tipo, todavía. */
export const CHART_TYPE_SWITCHABLE = new Set([
  'mkt-chart-origin-bar',
  'mkt-chart-source-pie',
  'mkt-chart-groups',
  'mkt-chart-campaign',
]);

export function isTabHidden(prefs, tabKey) {
  return (prefs?.tabs_hidden || []).includes(tabKey);
}

export function isChartHidden(prefs, tabKey, chartId) {
  return (prefs?.charts_hidden?.[tabKey] || []).includes(chartId);
}

export function chartTypeOverride(prefs, chartId) {
  return prefs?.chart_types?.[chartId] || null;
}

/**
 * Gráfico FIJO representativo por tab para el Reporte Ejecutivo (SaaS) --
 * a propósito NO configurable por dashboard_prefs: el Ejecutivo ignora la
 * personalización del usuario, siempre son los mismos 5.
 *
 * "ventas" NO usa "Top 5 vendedores" -- ese gráfico vive en `direccion`
 * (dir-chart-top), no en Ventas (verificado en dashboardSections.js más
 * arriba: Ventas no tiene ningún chart con ese nombre). Se usa
 * ventas-chart-pipeline como representativo real de Ventas.
 * "direccion" usa dir-chart-exec ("Resumen ejecutivo") -- coincide
 * literalmente con "KPIs principales o gráfico ejecutivo" del pedido.
 */
export const EXECUTIVE_FIXED_CHART_IDS = {
  marketing: 'mkt-chart-monthly',
  ventas: 'ventas-chart-pipeline',
  ia: 'ia-chart-channels',
  direccion: 'dir-chart-exec',
  callforce: 'cf-chart-interest-donut',
};

export function getExecutiveFixedChart(sectionKey) {
  const section = DASHBOARD_SECTIONS.find((s) => s.key === sectionKey);
  const chartId = EXECUTIVE_FIXED_CHART_IDS[sectionKey];
  return section?.charts.find((c) => c.id === chartId) || null;
}
